import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import JsBarcode from 'jsbarcode'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemCodigoBarras {
  empcod: number; serie: string; numero: number; cliCod: string; fornecedor: string
  pnvLin: number; itsCod: number; descricao: string
  quantidade: number; dataRecebimento: string | null
}

interface LinhaEditavel extends ItemCodigoBarras {
  selecionado: boolean
  qtdeImprimir: number
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function fmtDataBr(v: string | null) {
  if (!v) return '-'
  return v.slice(0, 10).split('-').reverse().join('/')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

function gerarBarcodePng(codigo: string): string {
  const canvas = document.createElement('canvas')
  JsBarcode(canvas, codigo, { format: 'CODE128', displayValue: false, height: 40, margin: 0 })
  return canvas.toDataURL('image/png')
}

export default function EmissaoCodigosBarras() {
  const { empresaCod } = useEmpresa()
  const [dataDe, setDataDe] = useState(hojeISO())
  const [dataAte, setDataAte] = useState(hojeISO())
  const [notaDe, setNotaDe] = useState('')
  const [notaAte, setNotaAte] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [itens, setItens] = useState<LinhaEditavel[]>([])

  // vindo do Lançamento de Notas (?numero=&serie=&cliCod=&data=): já busca a nota com os itens marcados
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const numero = searchParams.get('numero')
    if (!numero) return
    const data = searchParams.get('data') || hojeISO()
    setDataDe(data); setDataAte(data); setNotaDe(numero); setNotaAte(numero)
    setFornecedorCod(searchParams.get('cliCod') || '')
    buscar({
      empcod: Number(searchParams.get('empcod')) || empresaCod || 1, dataDe: data, dataAte: data,
      notaDe: Number(numero), notaAte: Number(numero),
      ...(searchParams.get('cliCod') ? { cliCod: searchParams.get('cliCod')! } : {}),
      ...(searchParams.get('serie') ? { serie: searchParams.get('serie')! } : {}),
    }, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function buscar(override?: Record<string, string | number>, marcar = false) {
    setErro(''); setBuscando(true); setItens([])
    try {
      const params: Record<string, string | number> = override ?? { empcod: empresaCod ?? 1, dataDe, dataAte }
      if (!override) {
        if (notaDe.trim()) params.notaDe = Number(notaDe)
        if (notaAte.trim()) params.notaAte = Number(notaAte)
        if (fornecedorCod) params.cliCod = fornecedorCod
      }
      const r = await api.get('/erp/codigos-barras/lista', { params })
      const dados: ItemCodigoBarras[] = r.data
      setItens(dados.map(it => ({
        ...it, selecionado: marcar, qtdeImprimir: it.quantidade || 1,
      })))
      if (dados.length === 0) setErro('Nenhum item com destino de estoque encontrado (combustível não entra na lista).')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar itens.')
    } finally {
      setBuscando(false)
    }
  }

  function marcarTodos(v: boolean) {
    setItens(prev => prev.map(it => ({ ...it, selecionado: v })))
  }
  function atualizarLinha(idx: number, patch: Partial<LinhaEditavel>) {
    setItens(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it))
  }

  // Zebra ZD230 (203 dpi): uma etiqueta 50×30 mm por página, coluna única — o gap de 3 mm
  // entre etiquetas é do próprio rolo (sensor da impressora), não vai no layout
  function gerarEtiquetasZebra() {
    const selecionados = itens.filter(it => it.selecionado && it.qtdeImprimir >= 1)
    if (selecionados.length === 0) { setErro('Marque ao menos um item com quantidade >= 1.'); return }
    const total = selecionados.reduce((s, it) => s + it.qtdeImprimir, 0)
    if (total > 300) { setErro(`Isso geraria ${total} etiquetas de uma vez (limite 300) — reduza a quantidade ou selecione menos itens.`); return }
    setErro('')
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;')
    const barras = new Map<number, string>()
    const etiquetas: string[] = []
    for (const it of selecionados) {
      const cod = String(it.itsCod).padStart(6, '0')
      if (!barras.has(it.itsCod)) {
        const canvas = document.createElement('canvas')
        JsBarcode(canvas, cod, { format: 'CODE128', displayValue: false, width: 3, height: 90, margin: 0 })
        barras.set(it.itsCod, canvas.toDataURL('image/png'))
      }
      for (let i = 0; i < it.qtdeImprimir; i++) {
        etiquetas.push(`<div class="etq">
  <div class="cod">${cod}</div>
  <div class="desc">${esc((it.descricao || '').slice(0, 34))}</div>
  <div class="linha"><span><b>NF:</b> ${it.numero}</span><span><b>Data:</b> ${fmtDataBr(it.dataRecebimento)}</span></div>
  <div class="forn">${esc((it.fornecedor || '').slice(0, 30))}</div>
  <img class="barra" src="${barras.get(it.itsCod)}" />
</div>`)
      }
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas Zebra</title>
<style>
  @page { size: 50mm 30mm; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; }
  .etq { width: 50mm; height: 30mm; box-sizing: border-box; padding: 1.2mm 2mm 1mm; overflow: hidden;
         display: flex; flex-direction: column; align-items: center; page-break-after: always; break-after: page; }
  .etq:last-child { page-break-after: auto; break-after: auto; }
  .cod { font-size: 13pt; font-weight: 700; line-height: 1; }
  .desc { font-size: 7pt; font-weight: 700; margin-top: 0.6mm; white-space: nowrap; max-width: 46mm; overflow: hidden; }
  .linha { width: 46mm; display: flex; justify-content: space-between; font-size: 6.5pt; margin-top: 0.4mm; }
  .forn { font-size: 6.5pt; margin-top: 0.2mm; white-space: nowrap; max-width: 46mm; overflow: hidden; }
  .barra { width: 40mm; height: 10mm; margin-top: 0.8mm; image-rendering: pixelated; }
</style></head>
<body onload="window.print()">${etiquetas.join('')}</body></html>`
    const janela = window.open('', '_blank')
    if (!janela) { setErro('O navegador bloqueou a janela de impressão — libere pop-ups pra esse site.'); return }
    janela.document.write(html)
    janela.document.close()
  }

  function gerarEtiquetas() {
    const selecionados = itens.filter(it => it.selecionado && it.qtdeImprimir >= 1)
    if (selecionados.length === 0) {
      setErro('Marque ao menos um item com quantidade >= 1.')
      return
    }
    const totalEtiquetas = selecionados.reduce((s, it) => s + it.qtdeImprimir, 0)
    if (totalEtiquetas > 300) {
      setErro(`Isso geraria ${totalEtiquetas} etiquetas de uma vez (limite 300) — reduza a quantidade ou selecione menos itens.`)
      return
    }

    // Um código de barras só precisa ser desenhado uma vez por Item — reaproveita
    // a mesma imagem PNG pra todas as cópias/etiquetas repetidas desse item.
    const barcodePorItem = new Map<number, string>()
    for (const it of selecionados) {
      if (!barcodePorItem.has(it.itsCod)) {
        barcodePorItem.set(it.itsCod, gerarBarcodePng(String(it.itsCod)))
      }
    }

    const etiquetasHtml: string[] = []
    for (const it of selecionados) {
      const png = barcodePorItem.get(it.itsCod)!
      for (let i = 0; i < it.qtdeImprimir; i++) {
        etiquetasHtml.push(`
          <div class="etiqueta">
            <div class="cod">${it.itsCod}</div>
            <img src="${png}" class="barra" />
            <div class="desc">${(it.descricao || '').slice(0, 40)}</div>
            <div class="rodape">NF ${it.numero} · ${(it.fornecedor || '').slice(0, 22)}</div>
            <div class="rodape">${fmtDataBr(it.dataRecebimento)}</div>
          </div>
        `)
      }
    }

    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Etiquetas</title>
<style>
  @page { size: auto; margin: 8mm; }
  body { margin: 0; font-family: Arial, sans-serif; }
  .grade { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
  .etiqueta {
    border: 1px dashed #999; border-radius: 3px; padding: 3mm;
    display: flex; flex-direction: column; align-items: center; gap: 1mm;
    break-inside: avoid; page-break-inside: avoid;
  }
  .cod { font-weight: 700; font-size: 12px; }
  .barra { width: 100%; max-width: 160px; }
  .desc { font-size: 9px; text-align: center; color: #333; }
  .rodape { font-size: 8px; color: #666; text-align: center; }
</style>
</head>
<body onload="window.print()">
  <div class="grade">${etiquetasHtml.join('')}</div>
</body></html>`

    const janela = window.open('', '_blank')
    if (!janela) { setErro('O navegador bloqueou a janela de impressão — libere pop-ups pra esse site.'); return }
    janela.document.write(html)
    janela.document.close()
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Emissão de Códigos de Barras</h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Data Inicial (Recebimento)" width={160}>
            <input style={inp} type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Data Final (Recebimento)" width={160}>
            <input style={inp} type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Nº Nota De" width={110}>
            <input style={inp} inputMode="numeric" value={notaDe} onChange={e => setNotaDe(e.target.value.replace(/\D/g, ''))} placeholder="0" />
          </Campo>
          <Campo rotulo="Nº Nota Até" width={110}>
            <input style={inp} inputMode="numeric" value={notaAte} onChange={e => setNotaAte(e.target.value.replace(/\D/g, ''))} placeholder="999999999" />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={300}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={() => buscar()} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {itens.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={gerarEtiquetasZebra} style={{ height: 32 }}
              title="Etiqueta 50×30 mm, uma por página, para a Zebra ZD230">
              Imprimir na Zebra
            </button>
            <button className="btn btn-secondary btn-sm" onClick={gerarEtiquetas} style={{ height: 32 }}
              title="Várias etiquetas por folha A4, em impressora comum">
              Imprimir em folha A4
            </button>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Só aparecem itens com destino de estoque. Na Zebra, imprima em tamanho real (100%).
            </span>
          </div>

          <table className="table" style={{ width: '100%', fontSize: '0.8rem', marginBottom: '1rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 40 }}>
                  <input type="checkbox" onChange={e => marcarTodos(e.target.checked)} />
                </th>
                <th style={{ textAlign: 'center', width: 90 }}>Item</th>
                <th style={{ textAlign: 'left' }}>Descrição</th>
                <th style={{ textAlign: 'center', width: 90 }}>Nota</th>
                <th style={{ textAlign: 'left', width: 200 }}>Fornecedor</th>
                <th style={{ textAlign: 'center', width: 100 }}>Recebimento</th>
                <th style={{ textAlign: 'center', width: 90 }}>Qtde</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => (
                <tr key={`${it.empcod}-${it.serie}-${it.numero}-${it.cliCod}-${it.pnvLin}`}
                  style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={it.selecionado} onChange={e => atualizarLinha(idx, { selecionado: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.itsCod}</td>
                  <td>{it.descricao}</td>
                  <td style={{ textAlign: 'center' }}>{it.numero}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.fornecedor}>{it.fornecedor}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataRecebimento)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="number" min={0} style={{ ...inp, width: 70, textAlign: 'center' }}
                      value={it.qtdeImprimir} onChange={e => atualizarLinha(idx, { qtdeImprimir: Number(e.target.value) || 0 })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
